import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { createGupshupTemplate } from "../../api/client";
import { buildTemplateExample, slugifyTemplateName } from "../../utils/whatsappTemplateCreate";

const LANGUAGE_OPTIONS = [
  { value: "pt_BR", labelKey: "createTemplateLangPtBr" },
  { value: "en", labelKey: "createTemplateLangEn" },
  { value: "en_US", labelKey: "createTemplateLangEnUs" },
  { value: "es", labelKey: "createTemplateLangEs" },
  { value: "es_AR", labelKey: "createTemplateLangEsAr" },
  { value: "es_MX", labelKey: "createTemplateLangEsMx" },
  { value: "pt_PT", labelKey: "createTemplateLangPtPt" },
  { value: "it", labelKey: "createTemplateLangIt" },
  { value: "fr", labelKey: "createTemplateLangFr" },
];

const AUTH_BODY = "{{1}} is your verification code.";

const emptyButton = () => ({ type: "QUICK_REPLY", text: "", url: "", phone_number: "", example: "" });

const INITIAL_FORM = {
  name: "",
  languageCode: "pt_BR",
  category: "UTILITY",
  header: "",
  content: "",
  example: "",
  footer: "",
  allowTemplateCategoryChange: true,
  addSecurityRecommendation: true,
  codeExpirationMinutes: "10",
  buttons: [],
};

function TemplatePreview({ form, t }) {
  const isAuth = form.category === "AUTHENTICATION";
  const body = isAuth ? AUTH_BODY : form.content;
  const header = isAuth ? "" : form.header;
  const footer = isAuth ? "" : form.footer;
  const buttons = isAuth
    ? [{ type: "OTP", text: t("channelForm.waCloudUi.createTemplateCopyOtp") }]
    : form.buttons;

  if (!header && !body && !footer && !buttons.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t("channelForm.waCloudUi.createTemplatePreviewEmpty")}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: "#ece5dd",
        minHeight: 160,
      }}
    >
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "0 12px 12px 12px",
          p: 1.5,
          boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
          maxWidth: "100%",
        }}
      >
        {header ? (
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 700 }}>
            {header}
          </Typography>
        ) : null}
        {body ? (
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {body}
          </Typography>
        ) : null}
        {footer ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
            {footer}
          </Typography>
        ) : null}
        {buttons.map((btn, idx) => (
          <Box
            key={`${btn.type}-${idx}`}
            sx={{
              mt: 1,
              py: 0.75,
              textAlign: "center",
              borderTop: "1px solid",
              borderColor: "divider",
              color: "primary.main",
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            {btn.text || t("channelForm.waCloudUi.createTemplateButtonText")}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default function CreateWhatsappTemplateDialog({
  open,
  channelId,
  initialDraft,
  onClose,
  onCreated,
}) {
  const { t } = useTranslation("settings");
  const [form, setForm] = useState(INITIAL_FORM);
  const [exampleTouched, setExampleTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    const draft = initialDraft && typeof initialDraft === "object" ? initialDraft : {};
    const category = ["UTILITY", "MARKETING", "AUTHENTICATION"].includes(
      String(draft.category || "").toUpperCase()
    )
      ? String(draft.category).toUpperCase()
      : "UTILITY";
    const content = category === "AUTHENTICATION" ? AUTH_BODY : String(draft.content || "");
    setForm({
      ...INITIAL_FORM,
      name: draft.name || "",
      languageCode: draft.languageCode || "pt_BR",
      category,
      content,
      example: content ? buildTemplateExample(content) : "",
    });
    setExampleTouched(false);
    setSubmitting(false);
    setError(null);
  }, [open, initialDraft]);

  const slug = useMemo(() => slugifyTemplateName(form.name), [form.name]);
  const isAuth = form.category === "AUTHENTICATION";

  const setField = (key, value) => {
    if (key === "category") setExampleTouched(false);
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "content" && !exampleTouched) {
        next.example = buildTemplateExample(value);
      }
      if (key === "category" && value === "AUTHENTICATION") {
        next.content = AUTH_BODY;
        next.example = buildTemplateExample(AUTH_BODY);
        next.header = "";
        next.footer = "";
        next.buttons = [];
      }
      if (key === "category" && value !== "AUTHENTICATION" && prev.category === "AUTHENTICATION") {
        next.content = "";
        next.example = "";
        next.buttons = [];
      }
      return next;
    });
  };

  const updateButton = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.map((btn, i) => (i === index ? { ...btn, ...patch } : btn)),
    }));
  };

  const addButton = (type) => {
    setForm((prev) => {
      if (prev.buttons.length >= 3) return prev;
      return { ...prev, buttons: [...prev.buttons, { ...emptyButton(), type }] };
    });
  };

  const removeButton = (index) => {
    setForm((prev) => ({
      ...prev,
      buttons: prev.buttons.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    if (!channelId) {
      setError(t("channelForm.waCloudUi.createTemplateNeedSave"));
      return;
    }
    if (!slug) {
      setError(t("channelForm.waCloudUi.createTemplateNameRequired"));
      return;
    }
    const content = isAuth ? AUTH_BODY : String(form.content || "").trim();
    if (!content) {
      setError(t("channelForm.waCloudUi.createTemplateBodyRequired"));
      return;
    }

    const buttons = isAuth
      ? undefined
      : form.buttons
          .map((btn) => {
            if (btn.type === "URL") {
              const payload = { type: "URL", text: btn.text, url: btn.url };
              if (btn.url && btn.url.includes("{{") && btn.example) payload.example = [btn.example];
              return payload;
            }
            if (btn.type === "PHONE_NUMBER") {
              return { type: "PHONE_NUMBER", text: btn.text, phone_number: btn.phone_number };
            }
            return { type: "QUICK_REPLY", text: btn.text };
          })
          .filter((btn) => btn.text);

    setSubmitting(true);
    setError(null);
    try {
      const result = await createGupshupTemplate(channelId, {
        elementName: slug,
        languageCode: form.languageCode,
        category: form.category,
        templateType: "TEXT",
        content,
        example: form.example || buildTemplateExample(content),
        header: isAuth ? undefined : form.header || undefined,
        footer: isAuth ? undefined : form.footer || undefined,
        buttons,
        allowTemplateCategoryChange: form.allowTemplateCategoryChange,
        addSecurityRecommendation: isAuth ? form.addSecurityRecommendation : undefined,
        codeExpirationMinutes: isAuth ? Number(form.codeExpirationMinutes) || undefined : undefined,
      });
      onCreated?.(result);
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("channelForm.waCloudUi.createTemplateError")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("channelForm.waCloudUi.createTemplateTitle")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("channelForm.waCloudUi.createTemplateCaption")}
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {t("channelForm.waCloudUi.createTemplateTipsTitle")}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <Typography component="li" variant="body2">
              {t("channelForm.waCloudUi.createTemplateTip1")}
            </Typography>
            <Typography component="li" variant="body2">
              {t("channelForm.waCloudUi.createTemplateTip2")}
            </Typography>
            <Typography component="li" variant="body2">
              {t("channelForm.waCloudUi.createTemplateTip3")}
            </Typography>
            <Typography component="li" variant="body2">
              {t("channelForm.waCloudUi.createTemplateTip4")}
            </Typography>
          </Box>
        </Alert>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Stack direction={{ xs: "column", md: "row" }} spacing={2.5} alignItems="stretch">
          <Stack spacing={1.75} sx={{ flex: 1, minWidth: 0 }}>
            <TextField
              label={t("channelForm.waCloudUi.createTemplateName")}
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              helperText={
                slug
                  ? t("channelForm.waCloudUi.createTemplateNameHelper", { slug })
                  : t("channelForm.waCloudUi.createTemplateNameHint")
              }
              fullWidth
              size="small"
              autoFocus
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <FormControl size="small" fullWidth>
                <InputLabel>{t("channelForm.waCloudUi.createTemplateLanguage")}</InputLabel>
                <Select
                  label={t("channelForm.waCloudUi.createTemplateLanguage")}
                  value={form.languageCode}
                  onChange={(e) => setField("languageCode", e.target.value)}
                >
                  {LANGUAGE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {t(`channelForm.waCloudUi.${opt.labelKey}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>{t("channelForm.waCloudUi.createTemplateCategory")}</InputLabel>
                <Select
                  label={t("channelForm.waCloudUi.createTemplateCategory")}
                  value={form.category}
                  onChange={(e) => setField("category", e.target.value)}
                >
                  <MenuItem value="UTILITY">
                    {t("channelForm.waCloudUi.createTemplateCategoryUtility")}
                  </MenuItem>
                  <MenuItem value="MARKETING">
                    {t("channelForm.waCloudUi.createTemplateCategoryMarketing")}
                  </MenuItem>
                  <MenuItem value="AUTHENTICATION">
                    {t("channelForm.waCloudUi.createTemplateCategoryAuthentication")}
                  </MenuItem>
                </Select>
                <FormHelperText>
                  {t("channelForm.waCloudUi.createTemplateCategoryHelper")}
                </FormHelperText>
              </FormControl>
            </Stack>
            {!isAuth ? (
              <TextField
                label={t("channelForm.waCloudUi.createTemplateHeader")}
                value={form.header}
                onChange={(e) => setField("header", e.target.value.slice(0, 60))}
                helperText={t("channelForm.waCloudUi.createTemplateHeaderHelper")}
                fullWidth
                size="small"
              />
            ) : null}
            <TextField
              label={t("channelForm.waCloudUi.createTemplateBody")}
              value={isAuth ? AUTH_BODY : form.content}
              onChange={(e) => setField("content", e.target.value.slice(0, 1024))}
              helperText={
                isAuth
                  ? t("channelForm.waCloudUi.createTemplateBodyAuthHelper")
                  : t("channelForm.waCloudUi.createTemplateBodyHelper")
              }
              fullWidth
              size="small"
              multiline
              minRows={3}
              disabled={isAuth}
            />
            <TextField
              label={t("channelForm.waCloudUi.createTemplateExample")}
              value={form.example}
              onChange={(e) => {
                setExampleTouched(true);
                setField("example", e.target.value.slice(0, 1024));
              }}
              helperText={t("channelForm.waCloudUi.createTemplateExampleHelper")}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
            {!isAuth ? (
              <TextField
                label={t("channelForm.waCloudUi.createTemplateFooter")}
                value={form.footer}
                onChange={(e) => setField("footer", e.target.value.slice(0, 60))}
                helperText={t("channelForm.waCloudUi.createTemplateFooterHelper")}
                fullWidth
                size="small"
              />
            ) : null}
            {isAuth ? (
              <>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.addSecurityRecommendation}
                      onChange={(e) => setField("addSecurityRecommendation", e.target.checked)}
                    />
                  }
                  label={t("channelForm.waCloudUi.createTemplateAuthSecurity")}
                />
                <TextField
                  label={t("channelForm.waCloudUi.createTemplateAuthExpiration")}
                  value={form.codeExpirationMinutes}
                  onChange={(e) => setField("codeExpirationMinutes", e.target.value.replace(/\D/g, "").slice(0, 2))}
                  helperText={t("channelForm.waCloudUi.createTemplateAuthExpirationHelper")}
                  size="small"
                  sx={{ maxWidth: 220 }}
                />
              </>
            ) : (
              <Box>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography variant="subtitle2">
                    {t("channelForm.waCloudUi.createTemplateButtons")}
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addButton("QUICK_REPLY")}
                      disabled={form.buttons.length >= 3}
                    >
                      {t("channelForm.waCloudUi.createTemplateButtonQuickReply")}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addButton("URL")}
                      disabled={form.buttons.length >= 3}
                    >
                      {t("channelForm.waCloudUi.createTemplateButtonUrl")}
                    </Button>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addButton("PHONE_NUMBER")}
                      disabled={form.buttons.length >= 3}
                    >
                      {t("channelForm.waCloudUi.createTemplateButtonPhone")}
                    </Button>
                  </Stack>
                </Stack>
                <Stack spacing={1}>
                  {form.buttons.map((btn, idx) => (
                    <Stack
                      key={`btn-${idx}`}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                      <TextField
                        size="small"
                        label={t("channelForm.waCloudUi.createTemplateButtonText")}
                        value={btn.text}
                        onChange={(e) => updateButton(idx, { text: e.target.value.slice(0, 25) })}
                        sx={{ flex: 1 }}
                      />
                      {btn.type === "URL" ? (
                        <>
                          <TextField
                            size="small"
                            label={t("channelForm.waCloudUi.createTemplateButtonUrlValue")}
                            value={btn.url}
                            onChange={(e) => updateButton(idx, { url: e.target.value })}
                            sx={{ flex: 1.4 }}
                          />
                          {String(btn.url || "").includes("{{") ? (
                            <TextField
                              size="small"
                              label={t("channelForm.waCloudUi.createTemplateButtonUrlExample")}
                              value={btn.example}
                              onChange={(e) => updateButton(idx, { example: e.target.value })}
                              sx={{ flex: 1 }}
                            />
                          ) : null}
                        </>
                      ) : null}
                      {btn.type === "PHONE_NUMBER" ? (
                        <TextField
                          size="small"
                          label={t("channelForm.waCloudUi.createTemplateButtonPhoneValue")}
                          value={btn.phone_number}
                          onChange={(e) => updateButton(idx, { phone_number: e.target.value })}
                          sx={{ flex: 1 }}
                        />
                      ) : null}
                      <IconButton
                        aria-label={t("channelForm.waCloudUi.createTemplateRemoveButton")}
                        onClick={() => removeButton(idx)}
                        size="small"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.allowTemplateCategoryChange}
                  onChange={(e) => setField("allowTemplateCategoryChange", e.target.checked)}
                />
              }
              label={t("channelForm.waCloudUi.createTemplateAllowCategoryChange")}
            />
          </Stack>
          <Box sx={{ width: { xs: "100%", md: 280 }, flexShrink: 0 }}>
            <Typography variant="overline" color="text.secondary">
              {t("channelForm.waCloudUi.createTemplatePreview")}
            </Typography>
            <TemplatePreview form={form} t={t} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {t("channelForm.waCloudUi.createTemplateCancel")}
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting
            ? t("channelForm.waCloudUi.createTemplateSubmitting")
            : t("channelForm.waCloudUi.createTemplateSubmit")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
